import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { loginUser } from "../../../../reducers/UserProfile/actions";
import { showErrorMsg, showSuccessMsg, showWarnMsg } from "../../../../utils/notifications";
import { setUserOTPObj, getUserOTPObj } from "../../../../utils/localStorage";
import analytics from "../../../../utils/analytics";
import { navigate } from "gatsby";

// Firebase imports
import {
  auth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
} from "../../../../utils/firebase";

import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import googlelogo from "../../../../assets/images/google-icon.png";

import "./style.css";

const EmbeddedLoginModal = ({ 
  isOpen, 
  onClose, 
  loginUser, 
  referral_code,
  onLoginSuccess 
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Prefill phone number from any existing OTP flow
  useEffect(() => {
    try {
      const otpData = getUserOTPObj && getUserOTPObj();
      if (otpData && typeof otpData.phoneNumber === "string") {
        const raw = otpData.phoneNumber.startsWith("+91")
          ? otpData.phoneNumber.replace("+91", "")
          : otpData.phoneNumber;
        if (/^\d{10}$/.test(raw)) {
          setPhoneNumber(raw);
        }
      }
    } catch (e) {
      // noop
    }
  }, []);

  // Process Firebase redirect result when modal opens (WebView/redirect flows)
  useEffect(() => {
    let mounted = true;
    if (!isOpen) return;
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (!mounted || !result || !result.user) return;
        const idToken = await result.user.getIdToken();
        analytics.trackPlatformEvent('GOOGLE_AUTH_SUCCESS', {
          method: 'redirect',
          user_id: result.user.uid,
          referral_code: referral_code,
          entry_point: 'embedded_modal'
        });
        try {
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('config', 'G-VSSESS01KJ', { user_id: result.user.uid });
            window.gtag('event', 'login_1', { method: 'google' });
          }
        } catch (_) {}
        await loginUser(idToken, referral_code);
        onLoginSuccess && onLoginSuccess();
        onClose();
      } catch (_) {}
    })();
    return () => { mounted = false; };
  }, [isOpen, loginUser, referral_code, onLoginSuccess, onClose]);

  // Reset any stale loading states each time the modal is opened
  useEffect(() => {
    if (isOpen) {
      try { setGoogleLoading(false); } catch (_) {}
      try { setLoading(false); } catch (_) {}
      // Clear any leftover reCAPTCHA instance
      try { if (window.recaptchaVerifier) { window.recaptchaVerifier.clear(); window.recaptchaVerifier = null; } } catch (_) {}
    }
  }, [isOpen]);

  // Listen for native mobile auth result (Firebase ID token) and log in
  useEffect(() => {
    if (!isOpen) return;
    const onSuccess = (event) => {
      try {
        const detail = event?.detail || {};
        const userToken = detail.userToken || detail.idToken || detail.token;
        if (userToken) {
          loginUser(userToken, referral_code);
          onLoginSuccess && onLoginSuccess();
          onClose();
        }
      } catch (e) {}
    };
    const onFailure = (event) => {
      try { setGoogleLoading(false); } catch (_) {}
      const err = event?.detail?.error || 'Authentication failed';
      showErrorMsg(err);
    };
    try {
      window.addEventListener('mobileAuthSuccess', onSuccess);
      window.addEventListener('mobileAuthFailure', onFailure);
    } catch (_) {}
    return () => {
      try { window.removeEventListener('mobileAuthSuccess', onSuccess); } catch (_) {}
      try { window.removeEventListener('mobileAuthFailure', onFailure); } catch (_) {}
    };
  }, [isOpen, loginUser, referral_code, onLoginSuccess, onClose]);

  const handleGoogleSignIn = async () => {
    if (googleLoading) return;
    try {
      setGoogleLoading(true);
      
      // Track Google auth click
      analytics.trackPlatformEvent('CLICK_CONTINUE_GOOGLE', {
        entry_point: 'embedded_modal',
        referral_code: referral_code
      });

      // Check if we're in a React Native WebView
      const isReactNativeWebView = window.ReactNativeWebView !== undefined;
      
      if (isReactNativeWebView) {
        // Delegate to native app via WebView bridge (expo-auth-session in mobile app)
        try {
          window.ReactNativeWebView.postMessage(JSON.stringify({
            type: 'AUTHENTICATION_REQUIRED',
            data: { provider: 'google' }
          }));
        } catch (_) {}
        return;
      }
      
      // For desktop browsers, use popup method
      // Create a fresh Google provider to avoid state issues after logout
      const googleProvider = new GoogleAuthProvider();
      // Ensure the account chooser shows to avoid silent/stale sessions
      try { googleProvider.setCustomParameters && googleProvider.setCustomParameters({ prompt: 'select_account' }); } catch (_) {}

      // Add a safety timeout in case popup is blocked or never resolves
      let safetyTimeout;
      const clearSafety = () => { if (safetyTimeout) { clearTimeout(safetyTimeout); safetyTimeout = null; } };
      safetyTimeout = setTimeout(() => {
        try { setGoogleLoading(false); } catch (_) {}
      }, 30000);

      let result;
      try {
        result = await signInWithPopup(auth, googleProvider).finally(clearSafety);
      } catch (popupErr) {
        // Fallback to redirect for environments where popups are flaky
        analytics.trackPlatformEvent('google_auth_popup_failed', {
          code: popupErr?.code,
          message: popupErr?.message,
          entry_point: 'embedded_modal'
        });
        clearSafety();
        setGoogleLoading(false);
        throw popupErr;
      }
      if (result) {
        const user = result.user;
        const idToken = await user.getIdToken();
        
        // Track Google auth success
        analytics.trackPlatformEvent('GOOGLE_AUTH_SUCCESS', {
          method: 'popup',
          user_id: user.uid,
          referral_code: referral_code,
          entry_point: 'embedded_modal'
        });

        // GA4: Emit login event
        try {
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('config', 'G-VSSESS01KJ', { user_id: user.uid });
            window.gtag('event', 'login_1', { method: 'google' });
          }
        } catch (e) {}

        await loginUser(idToken, referral_code);
        onLoginSuccess && onLoginSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Google Sign-In Error:", error);
      
      // Track Google auth failed
      analytics.trackPlatformEvent('GOOGLE_AUTH_FAILED', {
        method: 'popup',
        error_code: error.code,
        error_message: error.message,
        entry_point: 'embedded_modal'
      });

      if (error.code === 'auth/popup-blocked') {
        showErrorMsg("Popup blocked. Please allow popups for this site.");
      } else if (error.code === 'auth/popup-closed-by-user') {
        showWarnMsg("Sign-in cancelled.");
      } else {
        showErrorMsg("Authentication failed. Please try again.");
      }
      setGoogleLoading(false);
    } finally {
      // Ensure loading never stays stuck
      // If login succeeded, modal will close; otherwise, this keeps UI responsive
      try { if (googleLoading) setGoogleLoading(false); } catch (_) {}
    }
  };

  const signInWithPhone = async (pNumber) => {
    try {
      // Clear any existing reCAPTCHA widgets
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }

      // Initialize reCAPTCHA verifier
      const recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "embedded-recaptcha-container",
        {
          size: "invisible",
          callback: () => {
            console.log("reCAPTCHA verified successfully");
          },
          "expired-callback": () => {
            console.log("reCAPTCHA expired");
          },
        }
      );

      return await signInWithPhoneNumber(auth, pNumber, recaptchaVerifier);
    } catch (error) {
      console.error("Phone sign-in error:", error);
      window.recaptchaVerifier = null;
      throw error;
    }
  };

  const handlePhoneSubmit = async () => {
    if (loading) return;
    
    if (phoneNumber.length !== 10) {
      showWarnMsg("Please enter valid phone number.");
      return;
    }

    // Track phone number submit
    analytics.trackPlatformEvent('SUBMIT_PHONE_NUMBER', {
      phone_number: "+91" + phoneNumber,
      input_method: 'manual',
      referral_code: referral_code,
      entry_point: 'embedded_modal'
    });

    setLoading(true);
    try {
      const confirmationResult = await signInWithPhone("+91" + phoneNumber);

      if (!confirmationResult?.verificationId) {
        throw new Error("Failed to get verification ID");
      }

      // Store verification data for OTP screen
      const verificationData = {
        verificationId: confirmationResult.verificationId,
        phoneNumber: "+91" + phoneNumber,
        referral_code: referral_code,
        timestamp: Date.now(),
      };

      setUserOTPObj(verificationData);

      showSuccessMsg("OTP Sent! Please check your phone for the verification code.");
      
      // Redirect to OTP verification; do not close modal first to avoid flicker
      if (typeof window !== 'undefined') {
        window.location.href = '/verify-otp';
      }
    } catch (error) {
      console.error(error);
      showErrorMsg("There is some issue, Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    try {
      analytics.trackPlatformEvent('login_skip', {
        entry_point: 'embedded_modal'
      });
    } catch (_) {}
    try { onClose && onClose(); } catch (_) {}
    try {
      if (typeof window !== 'undefined') {
        window.location.replace('/');
      }
    } catch (_) {}
  };

  if (!isOpen) return null;

  return (
    <>
      {googleLoading && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.8)',
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
        >
          <div
            role="status"
            aria-live="polite"
            style={{
              padding: '20px 24px',
              borderRadius: 12,
              background: '#ffffff',
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
              color: '#4B235C',
              minWidth: 260,
              display: 'flex',
              alignItems: 'center',
              gap: 14
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 50 50"
              aria-hidden="true"
            >
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="#E6E0EB"
                strokeWidth="6"
              />
              <path
                d="M25 5 a20 20 0 0 1 0 40 a20 20 0 0 1 0 -40"
                fill="none"
                stroke="#6A2E7B"
                strokeWidth="6"
                strokeLinecap="round"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 25 25"
                  to="360 25 25"
                  dur="0.9s"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
            <div>
              <div style={{ fontWeight: 700 }}>Signing in with Google</div>
              <div style={{ fontSize: 12, color: '#6b6b6b', marginTop: 2 }}>Please wait…</div>
            </div>
          </div>
        </div>
      )}
      <div 
        className="embedded-login-overlay"
        onClick={(e) => {
          // Only close modal if clicking on the overlay itself, not on modal content
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div 
          className="embedded-login-modal"
          onClick={(e) => {
            // Prevent clicks on modal content from bubbling up to overlay
            e.stopPropagation();
          }}
        >
        <div className="embedded-login-header">
          <h2>Sign in to unlock gentle, trusted support made just for you and your baby.</h2>
        </div>
        
        <div className="embedded-login-content">
          <div className="embedded-login-form">
            <div className="embedded-input-group">
              <label className="embedded-label" htmlFor="embeddedPhoneInput">Phone Number</label>
              <TextField
                type="tel"
                inputMode="numeric"
                fullWidth
                id="embeddedPhoneInput"
                value={phoneNumber}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setPhoneNumber(onlyDigits);
                }}
                onFocus={() => {
                  try {
                    analytics.trackPlatformEvent('phone_input_focus', {
                      field_name: 'phone',
                      entry_point: 'embedded_modal',
                      has_value_at_focus: !!phoneNumber
                    });
                  } catch (_) {}
                }}
                placeholder="10-digit mobile number"
                disabled={loading}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">+91</InputAdornment>
                    ),
                    'aria-label': 'Phone number',
                  },
                }}
                required
              />
            </div>

            <button
              className="embedded-continue-btn"
              style={{ 
                backgroundColor: phoneNumber && !loading ? "#4B235C" : "gray", 
                opacity: loading ? 0.7 : 1, 
                pointerEvents: loading ? 'none' : 'auto' 
              }}
              onClick={handlePhoneSubmit}
              disabled={loading}
            >
              {loading ? "Sending OTP..." : "Continue"}
            </button>

            <div className="embedded-or-container">
              <div className="embedded-or-side-line"></div>
              <div className="embedded-or-cls">OR</div>
              <div className="embedded-or-side-line"></div>
            </div>

            <button
              className="embedded-continue-btn-google"
              style={{ opacity: googleLoading ? 0.6 : 1, pointerEvents: googleLoading ? 'none' : 'auto' }}
              onClick={handleGoogleSignIn}
            >
              <img src={googlelogo} alt="google logo" className="embedded-google-logo" />
              Continue with Google
            </button>

          <div
            role="button"
            onClick={handleSkip}
            style={{
              marginTop: 12,
              textAlign: 'center',
              color: '#6A2E7B',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer'
            }}
            aria-label="Skip sign in for now"
          >
            Skip for now
          </div>

            <div className="embedded-agree-cond">
              By continuing, I agree to Pukaar's <span 
                style={{ 
                  color: '#4B235C',
                  fontWeight: '500',
                  textDecoration: 'underline'
                }}
              >Privacy Policy</span> and
              <span 
                style={{ 
                  color: '#4B235C',
                  fontWeight: '500',
                  textDecoration: 'underline'
                }}
              > Terms of use</span>.
            </div>
          </div>
        </div>


        {/* Hidden reCAPTCHA container */}
        <div id="embedded-recaptcha-container"></div>
      </div>
    </div>
    </>
  );
};

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, { loginUser })(EmbeddedLoginModal);
